import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { segmentsApi } from '../../../apis';
import {
  InternalApiEntityGroup,
  InternalApiEntityType,
  InternalApiSegmentFeature,
  RecalculateEntityGroupRequest,
} from '../../../api-internal';
import { useMe } from '../../../organisation/useMe';
import { useDispatch } from 'react-redux';
import { showConfirmationModal } from '../../../confirmation-modal/confirmationModalReducer';
import dayjs from 'dayjs';
import reFetchingHelper from '../../../common/reFetchingHelper';

interface QuerySegmentsProps {
  entityType?: InternalApiEntityType;
  includeArchived?: boolean;
}

const getSegmentsQueryOptions = (
  entityType?: InternalApiEntityType,
  currentOrganisationId?: number,
  includeArchived?: boolean
) =>
  queryOptions({
    enabled: !!currentOrganisationId,
    staleTime: 1_000,
    queryKey: ['entity_groups', entityType, currentOrganisationId],
    queryFn: () => {
      return segmentsApi.findEntityGroups({ entityType, includeArchived });
    },
  });

const getSegmentQueryOptions = (code: string, currentOrganisationId?: number) =>
  queryOptions({
    enabled: !!currentOrganisationId,
    staleTime: 1_000,
    queryKey: ['segment', code, currentOrganisationId],
    queryFn: () => segmentsApi.getSegment({ code }),
  });

export const useQuerySegments = ({ entityType, includeArchived }: QuerySegmentsProps = {}) => {
  const { currentOrganisation } = useMe();

  return useQuery(getSegmentsQueryOptions(entityType, currentOrganisation?.id, includeArchived));
};

export const useQuerySegment = (code: string) => {
  const { currentOrganisation } = useMe();
  return useQuery({
    ...getSegmentQueryOptions(code, currentOrganisation?.id),
    refetchInterval: (query) => {
      const segment = query.state.data;
      if (segment && segment.fullCalculationState === 'STARTED') {
        return reFetchingHelper.getSegmentReFetchInterval();
      } else {
        return false;
      }
    },
  });
};

export const useUpdateSegment = () => {
  const queryClient = useQueryClient();
  const { currentOrganisation } = useMe();

  return useMutation({
    mutationFn: (internalApiEntityGroup: InternalApiEntityGroup) => {
      if (internalApiEntityGroup.id) {
        return segmentsApi.updateEntityGroup({
          groupId: internalApiEntityGroup.id!,
          internalApiEntityGroup: internalApiEntityGroup,
        });
      } else {
        return segmentsApi.addEntityGroup({ internalApiEntityGroup });
      }
    },
    onSuccess: async (segment) => {
      const { queryKey: segmentQueryKey } = getSegmentQueryOptions(segment.code, currentOrganisation?.id);
      await queryClient.invalidateQueries({
        queryKey: segmentQueryKey,
      });
      const { queryKey: segmentsQueryKey } = getSegmentsQueryOptions(segment.entityType, currentOrganisation?.id);
      await queryClient.invalidateQueries({
        queryKey: segmentsQueryKey,
      });
    },
  });
};

export const useDeleteSegment = () => {
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async (segment: InternalApiEntityGroup) => {
      const featuresUsingSegment = await segmentsApi
        .findSegmentFeatures({ code: segment.code! })
        .then((features) => features.map((it) => segmentFeatureToText(it)));
      if (featuresUsingSegment.length) {
        await dispatch(
          showConfirmationModal({
            title: `This Segment is currently used in ${featuresUsingSegment.join(', ')}. It cannot be deleted.`,
            onConfirm: null,
            cancelText: 'Close',
          })
        );
        return 'cancelled';
      } else {
        const confirmed = await dispatch(
          showConfirmationModal({
            title: 'Are you sure you want to archive this Segment?',
            confirmationText: 'Yes, archive',
            cancelText: 'No',
          })
        );

        if (confirmed) {
          await segmentsApi.deleteSegment({ code: segment.code! });
          return 'confirmed';
        } else {
          return 'cancelled';
        }
      }
    },
  });
};

export const useRecalculateSegment = (segment: InternalApiEntityGroup) => {
  const queryClient = useQueryClient();
  const { currentOrganisation } = useMe();
  const dispatch = useDispatch();

  return useMutation({
    mutationFn: async (days: number = 30) => {
      const isConfirmed = await dispatch(
        showConfirmationModal({
          title: 'Are you sure?',
          description: 'Are you sure you want to start Full Calculation for this Segment? It may take some time.',
          confirmationText: 'Yes, start Full Calculation',
          cancelText: 'No',
        })
      );

      if (isConfirmed) {
        const request: RecalculateEntityGroupRequest = {
          groupId: segment.id!,
        };
        if (segment.entityType === InternalApiEntityType.TRANSACTION) {
          request.internalApiEntityGroupRecalculationFilter = {
            startTime: dayjs()
              .subtract(days || 30, 'days')
              .toDate(),
            endTime: dayjs().toDate(),
          };
        }
        return await segmentsApi.recalculateEntityGroup(request);
      }
    },

    onSuccess: async () => {
      const { queryKey } = getSegmentQueryOptions(segment.code, currentOrganisation?.id);
      await queryClient.invalidateQueries({
        queryKey,
      });
    },
  });
};

const segmentFeatureToText = (feature: InternalApiSegmentFeature) => {
  switch (feature) {
    case InternalApiSegmentFeature.SCREENING_FLOWS:
      return 'Screening Flows';
    case InternalApiSegmentFeature.AUTOMATIC_RE_SCREENING:
      return 'Automatic Re-Screening';
    case InternalApiSegmentFeature.SCENARIOS:
      return 'Scenarios';
    case InternalApiSegmentFeature.LIBRARY_SCENARIOS:
      return 'Library Scenarios';
  }
};
